import { useForm } from 'new/elements/Form/useForm';
import { useCallback, useEffect, useState, useRef } from 'react';
import { shouldRenderRunAction } from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';
import {
  ADD_DATA_SOURCE_REQUEST,
  ADD_SINK_REQUEST,
  GET_DATA_SOURCE_REQUEST,
  GET_SINK_REQUEST,
  PROBE_GET_TREE_REQUEST,
  UPDATE_SINK_INFO_REQUEST,
  UPDATE_SOURCE_INFO_REQUEST,
} from 'js/constants/actionTypes';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import { FORM_TYPE_CREATE_REPLICATION } from 'js/constants/formTypes';
import { isEmpty } from 'lodash';
import useFlowsAdapter from 'new/components/flows/adapter/useFlowsAdapter';
import createDatabaseService from 'new/components/flows/configure/Database/service/createDatabaseService';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import createFlowService from 'new/components/flows/service/createFlowService';
import flowTypes from 'new/components/flows/types/flowTypes';
import { createResourceSetupService } from 'new/components/flows/types/replication/CreateReplicationFlow/ResourceSetup/service/createResourceSetupService';
import { useHeaderMetaConfig } from 'new/components/flows/context/useHeaderMetaConfig';
import useFlowParams from 'new/components/flows/useFlowParams';
import useFlows from 'new/components/flows/useFlows';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { usePreviousPath } from 'new/hooks/usePreviousPath';
import useFlowQueryParams from 'new/components/flows/useFlowQueryParams';
import useSchemas from 'new/state/dataSetSchemas/useSchemas';
import usePaging from 'new/state/paging/repository/usePaging';
import useProbe from 'new/state/probe/repository/useProbe';
import useRequests from 'new/state/request/repository/useRequests';
import { useHistory } from 'react-router-dom';
import useReplication from '../../useReplication';
import { useSelector } from 'react-redux';
import { useChangeDetection } from 'new/components/flows/hooks/useChangeDetection';
import useAppDispatch from 'js/hooks/useAppDispatch';
import {
  assignFlowToProjectIfPresent,
  redirectToProjectIfNeeded,
} from 'new/utils/projectAssignment';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { withUiModeCode, withUiModeDefault } from 'new/utils/uiMode';

export default function useResourceSetup() {
  const dispatch = useAppDispatch();
  const currentProjectId = useSelector(getCurrentProjectId);
  const { name: metaName, description: metaDescription } = useHeaderMetaConfig();

  const {
    sourceConfig,
    selectedConnection,
    sourceConfigFields,
    sinkConfig,
    selectedBucket,
    selectedTable,
    createSource: requestSourceCreation,
    updateSource: requestSourceUpdate,
    createSink: requestSinkCreation,
    updateSink: requestSinkUpdate,
    connectionType,
    nexset,
    source,
    sink,
    isNewTable,
    credentialId,
    selectedCredential,
    updateFormData,
  } = useReplication();
  const { resourceType, id } = useFlowParams();
  const { getProbeData } = useProbe();
  const { showError } = useRequests();
  const [initialLoading, setInitialLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // SaveConfirmation modal state
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const pendingSaveRef = useRef<((runWithIngestion: boolean) => Promise<boolean>) | null>(null);

  const { getSinkConfigFields, getMetaInfoById } = usePaging();

  // Get the current runWithIngestion value from form data
  const formData = useSelector((state: any) => state.forms[FORM_TYPE_CREATE_REPLICATION]?.data);
  const currentRunWithIngestion = formData?.runWithIngestion || false;

  const probe = getProbeData(credentialId);
  const flowType = flowTypes.replication;

  const metaInfo = getMetaInfoById(selectedConnection);
  const connectionCategory = metaInfo?.connectionCategory;
  const sinkConfigFields = getSinkConfigFields(selectedConnection, flowType as string);

  const {
    onSourceConfigChanged: updateSourceConfig,
    onSinkConfigChanged: updateSinkConfig,
    setupInitialFormData,
    onBucketSelected,
    onTableSelected,
    validate,
    cleanForm,
  } = useFlowsSetup(FORM_TYPE_CREATE_REPLICATION, flowType);
  const {
    setLoaderMessages,
    overrideNextFunction,
    setConfirmButtonTitle,
    setShowLeaveConfirmation,
    sendEvent,
  } = useForm();
  const history = useHistory();
  const currentOrg = useCurrentOrg();
  const { flowId } = useFlowQueryParams();
  const { requestResources } = useFlowsAdapter();
  const { onSinkMappingUpdated } = useFlows(updateFormData);
  const flowService = createFlowService();
  const databaseService = createDatabaseService();
  const { getNexsetSchemaById } = useSchemas();
  const service = createResourceSetupService();
  const previousPath = usePreviousPath();
  const { isModal, onDismiss } = useFlowParams();

  // Get change detection for the form
  const { hasChanges } = useChangeDetection(FORM_TYPE_CREATE_REPLICATION);

  useEffect(() => {
    setLoaderMessages({
      [UPDATE_SOURCE_INFO_REQUEST]: 'Updating source info',
      [GET_SINK_REQUEST]: 'Loading destination',
      [GET_DATA_SOURCE_REQUEST]: 'Loading source',
      [PROBE_GET_TREE_REQUEST]: isEmpty(probe.data)
        ? `Connecting to ${metaInfo.displayName}`
        : null,
      [ADD_DATA_SOURCE_REQUEST]: 'Creating source',
      [UPDATE_SINK_INFO_REQUEST]: 'Updating destination info',
      [ADD_SINK_REQUEST]: 'Creating destination',
    });
  }, [probe.loading]);

  useEffect(() => {
    if (!sink && connectionType === 'SINK' && source) {
      setConfirmButtonTitle('Done');
    } else if ((sink && connectionType === 'SINK') || (source && connectionType === 'SOURCE')) {
      setConfirmButtonTitle('Save');
    } else {
      setConfirmButtonTitle('Continue');
    }
  }, [sink, source, connectionType]);

  const setup = async () => {
    setInitialLoading(true);

    const requestParams = { resourceType, id, flowId };
    const resources = await requestResources(requestParams);

    if (!id && !flowId) {
      setInitialLoading(false);
      return;
    }

    /**
     * Only initialize the FormData if there are valid resources
     * Form is being initialized in the useFlowsSetup hook [onSelectConnection call]
     * It it is called again it will override the form data
     */
    setupInitialFormData(resources);
    setInitialLoading(false);
  };

  useEffect(() => {
    setup();
  }, [resourceType]);

  const buildSourceInfo = useCallback(() => {
    const { name, description, sourceName, sourceDescription, ...sourceConfigValues } =
      sourceConfig || {};

    let newSourceConfig = flowService.createDefaultSourceConfig({
      sourceConfig: sourceConfigValues,
      configFields: sourceConfigFields,
      selectedConnection,
      orgId: currentOrg.id,
    });

    if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
      newSourceConfig = databaseService.buildSourceConfig({
        sourceConfig: sourceConfigValues,
        configFields: sourceConfigFields,
      });
    }

    const sourceConfigWithMode = withUiModeDefault(newSourceConfig);

    return {
      ...(source?.id ? { id: source.id } : {}),
      name:
        metaName ||
        name ||
        sourceName ||
        flowService.getSourceName({
          selectedBucket,
          selectedTable,
          selectedCredential,
          connectionCategory,
        }),
      description: metaDescription || description || sourceDescription || '',
      flow_type: 'replication',
      source_config: sourceConfigWithMode,
    };
  }, [
    flowService,
    databaseService,
    source,
    selectedBucket,
    selectedTable,
    selectedCredential,
    connectionCategory,
    sourceConfig,
    sourceConfigFields,
    selectedConnection,
    currentOrg,
    metaName,
    metaDescription,
  ]);

  const buildSinkInfo = () => {
    let { name, description, sourceName, sourceDescription, ...sink_config } = sinkConfig;
    sink_config = withUiModeDefault(sink_config);
    const { sink_type = selectedConnection } = sink_config;

    let sinkInfo: any = {
      ...(sink?.id ? { id: sink.id } : {}),
      name:
        metaName ||
        name ||
        sourceName ||
        sink?.name ||
        flowService.getSinkName({
          selectedBucket,
          selectedTable,
          dataMapSettings: { name: '' },
          connectionCategory,
          selectedCredential,
          metaInfo,
          selectedConnection,
        }),
      flow_type: 'replication',
      description: metaDescription || description || sourceDescription || '',
      sink_type,
      sink_config,
    };

    if (isNewTable) {
      sinkInfo.create_destination = 'true';
    }

    if (!sink?.id) {
      sinkInfo.data_set_id = nexset?.id;
    }

    return sinkInfo;
  };

  const updateSource = useCallback(
    async (runWithIngestion = false) => {
      const sourceInfo = buildSourceInfo();
      return requestSourceUpdate(sourceInfo, runWithIngestion);
    },
    [buildSourceInfo, requestSourceUpdate],
  );

  const createSource = useCallback(() => {
    const sourceInfo = buildSourceInfo();
    return requestSourceCreation(sourceInfo);
  }, [buildSourceInfo, requestSourceCreation]);

  const updateSink = useCallback(() => {
    const sinkInfo = buildSinkInfo();
    return requestSinkUpdate(sinkInfo);
  }, [requestSinkUpdate, buildSinkInfo]);

  const createSink = useCallback(() => {
    const sinkInfo = buildSinkInfo();
    return requestSinkCreation(sinkInfo);
  }, [requestSinkCreation, buildSinkInfo]);

  const navigateAfterSaveSource = async (newSource) => {
    if (!!newSource?.id) {
      // Ensure Form is dismissed before navigation to prevent Exit Workflow modal
      setShowLeaveConfirmation(false);

      if (isModal) {
        onDismiss({ addedDataSourceId: newSource.id, ...newSource });
        return;
      }
      if (id) {
        // Editing existing source - just go back
        setTimeout(() => {
          history.goBack();
        }, 0);
      } else {
        // Creating new source - navigate to edit screen if it created a new flow
        try {
          const flowNodeId = newSource?.origin_node_id || newSource?.flow_node_id || flowId;

          setIsRedirecting(true);
          const assignedProjectId = await assignFlowToProjectIfPresent({
            dispatch,
            flowNodeId,
            currentProjectId,
          });
          if (assignedProjectId) {
            setTimeout(() => {
              redirectToProjectIfNeeded(history, assignedProjectId, dispatch);
            }, 0);
            return;
          }
        } catch (error) {
          console.error('Failed to assign flow to project:', error);
        } finally {
          setIsRedirecting(false);
        }
        // Use setTimeout to ensure form dismissal happens before navigation
        setTimeout(() => {
          const flowOriginId = newSource?.origin_node_id || null;
          history.replace(
            flowService.getPathToGoAfterCreation(previousPath, undefined, {
              resourceType: 'source',
              flowType: 'replication',
              flowOriginId,
            }),
          );
        }, 0);
      }
    }
  };

  const navigateAfterSaveSink = async (newSink) => {
    if (!!newSink?.id) {
      // Ensure form is dismissed before navigation to prevent Exit Workflow modal
      setShowLeaveConfirmation(false);

      if (isModal) {
        onDismiss({ addedDataSinkId: newSink.id, ...newSink });
        return;
      }
      if (id) {
        // Use setTimeout to ensure form dismissal happens before navigation
        setTimeout(() => {
          history.goBack();
        }, 0);
      } else {
        try {
          const flowNodeId = newSink?.origin_node_id || newSink?.flow_node_id || flowId;

          setIsRedirecting(true);
          const assignedProjectId = await assignFlowToProjectIfPresent({
            dispatch,
            flowNodeId,
            currentProjectId,
          });
          if (assignedProjectId) {
            setTimeout(() => {
              redirectToProjectIfNeeded(history, assignedProjectId, dispatch);
            }, 0);
            return;
          }
        } catch (error) {
          console.error('Failed to assign flow to project:', error);
        } finally {
          setIsRedirecting(false);
        }
        const newPath = flowService.getPathToGoAfterCreation(previousPath);

        // Use setTimeout to ensure form dismissal happens before navigation
        setTimeout(() => {
          history.replace(newPath);
        }, 0);
      }
    }
  };

  const requestSource = useCallback(
    async (runWithIngestion = false) => {
      try {
        let newSource;
        if (source) {
          // For existing sources, pass runWithIngestion to updateSource

          newSource = await updateSource(runWithIngestion);
        } else {
          // For new sources, don't pass runWithIngestion (not supported)

          newSource = await createSource();
        }

        navigateAfterSaveSource(newSource);
        if (newSource?.id) {
          cleanForm(newSource.id);
        }
        return !!newSource?.id;
      } catch (e) {
        return false;
      }
    },
    [source, updateSource, createSource],
  );

  const requestSink = useCallback(async () => {
    try {
      let newSink;
      if (!!sink?.id) {
        newSink = await updateSink();
      } else {
        newSink = await createSink();
      }
      cleanForm(newSink.id);
      navigateAfterSaveSink(newSink);
      return !!newSink?.id;
    } catch (e) {
      return false;
    }
  }, [sink, updateSink, createSink]);

  const performSave = useCallback(
    async (runWithIngestion: boolean) => {
      if (!validate(showError)) {
        return false;
      }

      if (connectionType === 'SOURCE') {
        return await requestSource(runWithIngestion);
      }

      return await requestSink();
    },
    [requestSink, requestSource, validate, connectionType],
  );

  const onNext = useCallback(async () => {
    // Check if we should show save confirmation
    const shouldShowSaveConfirmation = !!(
      (source || id) && // Either we have a source object or we're in edit mode (have an id)
      source?.status === 'ACTIVE' && // Source is active (only check if source exists)
      shouldRenderRunAction(source, connectionCategory) &&
      hasChanges && // Only show confirmation if there are actual changes
      !isSaving
    );

    if (shouldShowSaveConfirmation) {
      setShowSaveConfirmation(true);
      // Store the save function to call later and return a promise that resolves when save completes
      return new Promise<boolean>((resolve) => {
        pendingSaveRef.current = async (runWithIngestion: boolean) => {
          const result = await performSave(runWithIngestion);
          if (result) {
            setShowLeaveConfirmation(false);
            cleanForm(source?.id);
          }
          resolve(result);
          return result;
        };
      });
    }

    // No save confirmation needed, proceed with normal save

    const result = await performSave(currentRunWithIngestion);

    // If save was successful, complete the form flow to prevent exit modal
    if (result) {
      setShowLeaveConfirmation(false);
      cleanForm(source?.id);
    }

    return result;
  }, [
    performSave,
    source,
    connectionCategory,
    isSaving,
    currentRunWithIngestion,
    setShowLeaveConfirmation,
    cleanForm,
  ]);

  const handleSaveConfirmation = useCallback(
    async (runWithIngestion: boolean) => {
      setIsSaving(true);
      setShowSaveConfirmation(false);
      updateFormData({ runWithIngestion });

      // Call the pending save function if it exists
      let result;
      if (pendingSaveRef.current) {
        result = await pendingSaveRef.current(runWithIngestion);
        pendingSaveRef.current = null;
      } else {
        // Fallback to direct save if no pending function
        result = await performSave(runWithIngestion);
      }
      setIsSaving(false);

      if (result) {
        setShowLeaveConfirmation(false);
        // Reset form data to prevent change detection from triggering Exit Workflow modal
        cleanForm(source?.id);

        // Return true to indicate successful completion and set finished state
        // This will prevent the exit workflow modal from showing
        return true;
      }

      return false;
    },
    [updateFormData, performSave, setShowLeaveConfirmation, sendEvent, cleanForm],
  );

  useEffect(() => {
    return () => {
      overrideNextFunction(null);
    };
  }, []);

  useEffect(() => {
    overrideNextFunction(onNext);

    // Cleanup: Reset override function when dependencies change
    return () => {
      overrideNextFunction(null);
    };
  }, [onNext, overrideNextFunction]);

  const onSinkMappingChanged = (newSinkMapping, auto) => {
    onSinkMappingUpdated({
      connectionCategory,
      sinkConfig,
      isEditMode: !!sink?.id,
    })(newSinkMapping, auto);
  };

  const onSourceConfigChanged = useCallback(
    (newSourceConfig: Record<string, any>) => {
      const sourceConfigWithMode = withUiModeDefault(newSourceConfig);
      updateSourceConfig(sourceConfigWithMode);
    },
    [updateSourceConfig],
  );

  const onSinkConfigChanged = useCallback(
    (newSinkConfig: Record<string, any>, field: any, value: any) => {
      const sinkConfigWithMode = withUiModeDefault(newSinkConfig);
      updateSinkConfig(sinkConfigWithMode, field, value);
    },
    [updateSinkConfig],
  );

  const nexsetSchema = getNexsetSchemaById(nexset?.id);

  /**
   * function responsible to create the resource on JSON Config Mode
   */
  const onSaveAdvancedMode = async (data: any) => {
    const advancedConfig = withUiModeCode(data);

    if (connectionType === 'SOURCE') {
      updateSourceConfig(advancedConfig);
      return;
    }

    updateSinkConfig(advancedConfig, null, null);
  };

  const onError = useCallback(() => {}, []);

  // Reset saving flag when form is dismissed or when there are no more changes
  useEffect(() => {
    if (isSaving && !source) {
      setIsSaving(false);
    }
  }, [isSaving, source]);

  // Prevent form from showing leave confirmation when saving
  useEffect(() => {
    if (isSaving) {
      setShowLeaveConfirmation(false);
    }
  }, [isSaving]);

  return {
    initialLoading,
    isRedirecting,
    nexsetSchema,
    onSinkConfigChanged,
    onBucketSelected,
    sinkConfigFields,
    onTableSelected,
    onSinkMappingChanged,
    allowCustomPrefixEditor: service.canEditPrefix({
      connectionCategory,
      selectedBucket,
      sinkConfig,
    }),
    onSourceConfigChanged,
    onSaveAdvancedMode,
    // SaveConfirmation modal props
    showSaveConfirmation,
    handleSaveConfirmation,
    onCloseSaveConfirmation: () => setShowSaveConfirmation(false),
  };
}
